import { useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import useSubstratePanelStore from '@/stores/substratePanelStore';
import { ROUTES } from '@/utils/constants';
import { useAuth } from '@/features/auth/hooks';
import { useSchool } from '@/contexts/SchoolContext';
import { hasAuthoritativeSchoolAdminAccess } from '@/services/domain/schoolMembership';
import CalendarPanel from './substratePanels/CalendarPanel';
import PeoplePanel from './substratePanels/PeoplePanel';
import AccessCodesPanel from './substratePanels/AccessCodesPanel';
import TimetablePanel from './substratePanels/TimetablePanel';
import OrgChartPanel from './substratePanels/OrgChartPanel';
import DomainsPanel from './substratePanels/DomainsPanel';
import CurrenciesPanel from './substratePanels/CurrenciesPanel';
import WorkflowTemplatesPanel from './substratePanels/WorkflowTemplatesPanel';
import PoliciesPanel from './substratePanels/PoliciesPanel';

/**
 * SubstratePanel — right-edge slide-out, ambient reference + inline CRUD.
 *
 * A full-page surface may opt into a contextual companion. Calendar does so:
 * its full page owns Month/Week/Day while this panel owns Today/This week.
 * Other matching panels remain closed until they define equivalent additional
 * value instead of duplicating their full page.
 */

const PANEL_REGISTRY = {
  calendar: { title: 'Calendar', fullRoute: ROUTES.CALENDAR, Component: CalendarPanel, companionOnFullPage: true },
  people: { title: 'People', fullRoute: ROUTES.PEOPLE, Component: PeoplePanel, companionOnFullPage: true },
  accessCodes: { title: 'Access codes', fullRoute: ROUTES.ACCESS_CODES, Component: AccessCodesPanel },
  timetable: { title: 'Timetable', fullRoute: ROUTES.TIMETABLE, Component: TimetablePanel },
  orgChart: { title: 'Org chart', fullRoute: ROUTES.ORG_CHART, fullRouteAdmin: true, Component: OrgChartPanel },
  domains: { title: 'Domains', fullRoute: ROUTES.DOMAINS, Component: DomainsPanel },
  currencies: { title: 'Currencies', Component: CurrenciesPanel },
  policies: { title: 'Policies', fullRoute: ROUTES.POLICIES_ADMIN, Component: PoliciesPanel },
  workflows: { title: 'Flows', fullRoute: ROUTES.WORKFLOW_TEMPLATES, Component: WorkflowTemplatesPanel },
};

export default function SubstratePanel() {
  const location = useLocation();
  const { user } = useAuth();
  const { schoolId } = useSchool();
  const activePanel = useSubstratePanelStore((s) => s.activePanel);
  const panelContext = useSubstratePanelStore((s) => s.panelContext);
  const closePanel = useSubstratePanelStore((s) => s.closePanel);
  const openPanel = useSubstratePanelStore((s) => s.openPanel);
  const scrollRef = useRef(null);

  useLayoutEffect(() => {
    if (activePanel === 'people' && scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [activePanel, panelContext?.view, panelContext?.personKey]);

  useEffect(() => {
    if (!activePanel) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closePanel();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [activePanel, closePanel]);

  useEffect(() => {
    const panel = PANEL_REGISTRY[activePanel];
    if (panel?.fullRoute && location.pathname === panel.fullRoute && !panel.companionOnFullPage) closePanel();
    if (activePanel === 'people' && location.pathname === ROUTES.PEOPLE && !panelContext) {
      openPanel('people', { kind: 'people-workspace', view: 'overview', title: 'People tools' });
    }
  }, [activePanel, closePanel, location.pathname, openPanel, panelContext]);

  if (!activePanel) return null;
  const config = PANEL_REGISTRY[activePanel];
  if (!config) return null;
  if (config.fullRoute && location.pathname === config.fullRoute && !config.companionOnFullPage) return null;
  const isLearnerProfile = activePanel === 'people' &&
    panelContext?.kind === 'learner-profile' && panelContext.studentId;
  const isPeopleWorkspace = activePanel === 'people' && panelContext?.kind === 'people-workspace';
  const title = isLearnerProfile ? 'Behaviour profile' : (isPeopleWorkspace ? panelContext.title || 'People' : config.title);
  const fullRoute = isLearnerProfile
    ? `${ROUTES.STUDENT_PROFILE}/${panelContext.studentId}#behaviour`
    : config.fullRoute;
  const { Component } = config;

  return (
    <aside
      className="hidden lg:flex sticky top-20 self-start z-40 flex-col overflow-hidden bg-base-100 border-l border-base-300 shadow-xl shrink-0"
      style={{
        width: 'var(--substrate-panel-w)',
        height: 'calc(100vh - 5rem)',
      }}
      role="complementary"
      aria-label={`${title} panel`}
    >
      <header className="flex items-center justify-between px-4 py-2.5 border-b border-base-200">
        <h2 className="font-semibold text-sm">{title}</h2>
        <div className="flex items-center gap-1">
          {fullRoute && location.pathname !== fullRoute &&
            (!config.fullRouteAdmin || hasAuthoritativeSchoolAdminAccess(user, schoolId)) && (
            <Link
              to={fullRoute}
              className="btn btn-ghost btn-xs gap-1"
              title={`Open ${title.toLowerCase()} full page`}
            >
              <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
              Open full
            </Link>
          )}
          <button
            type="button"
            className="btn btn-ghost btn-xs btn-square"
            onClick={closePanel}
            aria-label="Close panel"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>
      </header>

      <div
        ref={scrollRef}
        data-testid="substrate-panel-scroll"
        className={`flex-1 min-h-0 overflow-y-auto overscroll-contain ${activePanel === 'people' ? 'pb-20' : ''}`}
      >
        <Component isMatchingFullRoute={location.pathname === config.fullRoute} />
      </div>
    </aside>
  );
}
